// src/components/OrderDetails.js
import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Grid,
    Button,
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    MenuItem,
    Stack,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import he from 'he';

// Mintsoft mappings
const mintsoftStatusMapping = {
    1: 'NEW',
    2: 'PRINTED',
    3: 'CANCELLED',
    4: 'DESPATCHED',
    7: 'HOLDING',
    9: 'ONBACKORDER',
    13: 'QUERYRAISED',
    15: 'AWAITINGPICKING',
    17: 'PICKED',
};

const mintsoftStatusColorMapping = {
    1: 'primary',
    2: 'primary',
    3: 'error',
    4: 'success',
    7: 'secondary',
    9: 'secondary',
    13: 'warning',
    15: 'info',
    17: 'primary',
};

const formatMintsoftTimestamp = (value) => {
    if (!value) return 'Unknown time';

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-GB');
};

const DetailTile = ({ label, children, sx = {} }) => (
    <Box
        sx={{
            p: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#fcfdff',
            minHeight: 84,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            ...sx,
        }}
    >
        <Typography
            variant="caption"
            sx={{
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: 0.35,
                textTransform: 'uppercase',
                mb: 0.75,
            }}
        >
            {label}
        </Typography>
        {typeof children === 'string' || typeof children === 'number' ? (
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.4 }}>
                {children}
            </Typography>
        ) : (
            children
        )}
    </Box>
);

// KornitX mappings
const kornitxStatusColorMapping = {
    'Received': 'primary',
    'In Production': 'primary',
    'Dispatched': 'success',
    'Cancelled': 'error',
    'On Hold': 'secondary',
    'QC Qurey': 'error',
    'Consolidating': 'secondary',
    'Not Found': 'warning',
    'Unknown': 'warning',
};

// Define the available shipping options for KornitX orders.
export const shippingCarriersOptions = [
    { value: 'Royal Mail', label: 'Royal Mail' },
    { value: 'Evri', label: 'Evri' },
].sort((a, b) => a.label.localeCompare(b.label));

// Define the shipping methods available for each carrier and sort them descending by label.
export const shippingMethodsByCarrier = {
    'Royal Mail': [
        { value: 'intl bus parcels bespoke postal', label: 'intl bus parcels bespoke postal' },
        { value: 'tracked 24', label: 'tracked 24' },
        { value: 'standard untracked 24', label: 'standard untracked 24' },
        { value: 'standard untracked 24 large letter', label: 'standard untracked 24 large letter' },
        { value: 'intl bus mail large letter zone pri', label: 'intl bus mail large letter zone pri' },
        { value: 'intl bus parcels zone sort pri', label: 'intl bus parcels zone sort pri' },
        { value: 'tracked 24 signature (age)', label: 'tracked 24 signature (age)' },
        { value: 'standard untracked 48', label: 'standard untracked 48' },
        { value: 'second class letter', label: 'second class letter' },
        { value: 'intl bus mail lo vol pri', label: 'intl bus mail lo vol pri' },
        { value: '', label: '-- --' },
    ].sort((a, b) => a.label.localeCompare(b.label)),

    'Evri': [
        { value: 'Next Day', label: 'Next Day' },
        { value: 'Standard Delivery', label: 'Standard Delivery' },
        { value: '', label: '-- --' },
    ].sort((a, b) => a.label.localeCompare(b.label)),
};


const OrderDetails = ({
    order,
    courierServices,
    selectedCourierService,
    setSelectedCourierService,
    numberOfParcels,
    setNumberOfParcels,
    isEditingShipment,
    onToggleEdit,
    onSaveShipment,
    checkingStatus,
    kornitxOrderStatus,
    fetchKornitxStatus,
    orderSource, // "mintsoft" or "kornitx"
    kxShippingCarrier,
    setKxShippingCarrier,
    kxShippingMethod,
    setKxShippingMethod,
    shipmentDraftIndicator,
}) => {
    if (!order) return null;

    if (orderSource === "kornitx") {
        // For KornitX orders, calculate total items from order.items.items
        const totalQtyKX =
            order.items?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

        return (
            <Card variant="outlined" sx={{ mb: 2, boxShadow: 1, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                KornitX Order ID: {order.id}
                            </Typography>
                            <Chip
                                label={order.status_name}
                                color={kornitxStatusColorMapping[order.status_name] || 'default'}
                                size="small"
                            />
                        </Box>
                        <Button variant="outlined" size="small" onClick={onToggleEdit}>
                            {isEditingShipment ? 'Cancel Edit' : 'Edit Shipping'}
                        </Button>
                    </Box>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" fontWeight="bold">
                                External Ref
                            </Typography>
                            <Typography variant="body2">{order.external_ref}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" fontWeight="bold">
                                Total Items
                            </Typography>
                            <Typography variant="body2">{totalQtyKX}</Typography>
                        </Grid>
                        {isEditingShipment ? (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Shipping Carrier"
                                        fullWidth
                                        size="small"
                                        value={kxShippingCarrier}
                                        onChange={(e) => {
                                            setKxShippingCarrier(e.target.value);
                                            // Optionally clear the shipping method if the carrier changes.
                                            setKxShippingMethod('');
                                        }}
                                    >
                                        {shippingCarriersOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Shipping Method"
                                        fullWidth
                                        size="small"
                                        value={kxShippingMethod}
                                        onChange={(e) => setKxShippingMethod(e.target.value)}
                                    >
                                        {kxShippingCarrier &&
                                            (shippingMethodsByCarrier[kxShippingCarrier] || []).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        {!kxShippingCarrier && (
                                            <MenuItem value="">
                                                <em>Select a carrier first</em>
                                            </MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" fontWeight="bold">
                                        Shipping Carrier
                                    </Typography>
                                    <Typography variant="body2">{order.shipping_carrier || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" fontWeight="bold">
                                        Shipping Method
                                    </Typography>
                                    <Typography variant="body2">{order.shipping_method || 'N/A'}</Typography>
                                </Grid>
                            </>
                        )}
                        {order.GiftMessage && (
                            <Grid item xs={12}>
                                <Typography variant="caption" fontWeight="bold">
                                    Gift Message
                                </Typography>
                                <Typography variant="body2">{he.decode(order.GiftMessage)}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    // Default: Mintsoft order details
    const {
        ID,
        OrderNumber,
        ExternalOrderReference,
        OrderStatusId,
        CourierServiceId,
        CourierServiceName,
        GiftMessages,
        OrderItems,
        NumberOfParcels,
        OrderQueries,
        OrderQueryComments,
        OrderQueryCommentsError,
    } = order;

    const totalQty =
        OrderItems?.reduce((sum, item) => sum + (item.Quantity || 0), 0) || 0;
    const totalWeight =
        OrderItems?.reduce((sum, item) => {
            const weight = item.product?.Weight || 0;
            return sum + weight * item.Quantity;
        }, 0) || 0;

    const currentCourierService =
        courierServices.find((s) => s.ID === (selectedCourierService || CourierServiceId));
    const parsedParcelCount = Number(numberOfParcels);
    const parcelCountError =
        numberOfParcels !== '' && (!Number.isInteger(parsedParcelCount) || parsedParcelCount < 1)
            ? 'Number of parcels must be a whole number greater than 0.'
            : parsedParcelCount > totalQty
                ? `Number of parcels cannot exceed total items (${totalQty}).`
                : '';
    const mintsoftStatusLabel = mintsoftStatusMapping[OrderStatusId] || `STATUS ${OrderStatusId}`;
    const mintsoftStatusColor = mintsoftStatusColorMapping[OrderStatusId] || 'default';
    const isMintsoftQueryRaised = OrderStatusId === 13;
    const sortedOrderQueryComments = Array.isArray(OrderQueryComments)
        ? [...OrderQueryComments].sort(
            (a, b) => new Date(b.LastUpdated || 0).getTime() - new Date(a.LastUpdated || 0).getTime()
        )
        : [];
    const latestOrderQuery = Array.isArray(OrderQueries) && OrderQueries.length > 0
        ? [...OrderQueries].sort(
            (a, b) => new Date(b.LastUpdated || 0).getTime() - new Date(a.LastUpdated || 0).getTime()
        )[0]
        : null;
    const hasGiftMessage = Boolean(
        GiftMessages &&
        String(GiftMessages).trim() &&
        String(GiftMessages).trim().toLowerCase() !== 'none'
    );

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                borderRadius: 3,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <CardContent
                sx={{
                    p: 1.75,
                    '&:last-child': { pb: 1.75 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    height: '100%',
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                            Order {ID}
                        </Typography>
                        <Chip
                            label={mintsoftStatusLabel}
                            color={mintsoftStatusColor}
                            size="small"
                            sx={{ fontWeight: 700 }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {isEditingShipment && onSaveShipment && (
                            <Button variant="contained" color="secondary" size="small" onClick={onSaveShipment}>
                                Save Shipment
                            </Button>
                        )}
                        <Button variant="outlined" size="small" onClick={onToggleEdit}>
                            {isEditingShipment ? 'Cancel' : 'Edit Shipment'}
                        </Button>
                    </Stack>
                </Box>

                {isMintsoftQueryRaised && (
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'warning.light',
                            backgroundColor: 'rgba(237, 108, 2, 0.08)',
                        }}
                    >
                        <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Dispatch is blocked because this order has a Mintsoft query raised.
                        </Typography>
                        {latestOrderQuery && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                Query {latestOrderQuery.ID} | {latestOrderQuery.UsersInvolved || 'No users listed'} | {formatMintsoftTimestamp(latestOrderQuery.LastUpdated)}
                            </Typography>
                        )}
                        <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 0.5 }}>
                            {sortedOrderQueryComments.length > 0 ? (
                                sortedOrderQueryComments.map((comment) => (
                                    <Box
                                        key={comment.ID}
                                        sx={{
                                            mb: 0.75,
                                            p: 1,
                                            borderRadius: 1.5,
                                            backgroundColor: '#fff',
                                            border: '1px solid',
                                            borderColor: 'warning.light',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                            {he.decode(comment.Comment || 'No comment text provided.')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {comment.LastUpdatedByUser || 'Unknown user'} | {formatMintsoftTimestamp(comment.LastUpdated)}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {OrderQueryCommentsError || 'No Mintsoft query comments returned for this order.'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {shipmentDraftIndicator?.isActive && (
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'info.light',
                            backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={0.75}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            sx={{ mb: 0.5 }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.dark' }}>
                                Using saved shipment draft from this browser.
                            </Typography>
                            <Chip
                                label="Local only until dispatch"
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        </Stack>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {shipmentDraftIndicator.changedCourier && (
                                <Chip
                                    label={`Mintsoft courier: ${shipmentDraftIndicator.originalCourierServiceName}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {shipmentDraftIndicator.changedParcels && (
                                <Chip
                                    label={`Mintsoft parcels: ${shipmentDraftIndicator.originalParcelCount}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                        <DetailTile label="Order Number">{OrderNumber || 'N/A'}</DetailTile>
                    </Grid>
                    <Grid item xs={6}>
                        <DetailTile label="External Ref">{ExternalOrderReference || 'N/A'}</DetailTile>
                    </Grid>
                    <Grid item xs={6}>
                        <DetailTile label="Total Items">{totalQty}</DetailTile>
                    </Grid>
                    <Grid item xs={6}>
                        <DetailTile label="Total Weight">
                            {totalWeight ? `${totalWeight.toFixed(2)} kg` : 'N/A'}
                        </DetailTile>
                    </Grid>
                    <Grid item xs={12}>
                        <DetailTile label="Courier Service">
                            {isEditingShipment ? (
                                <Autocomplete
                                    options={courierServices}
                                    getOptionLabel={(option) => option.Name}
                                    value={
                                        courierServices.find((s) => s.ID === selectedCourierService) ||
                                        null
                                    }
                                    onChange={(e, newVal) =>
                                        setSelectedCourierService(newVal ? newVal.ID : '')
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} size="small" variant="outlined" />
                                    )}
                                />
                            ) : (
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.4 }}>
                                    {currentCourierService?.Name || CourierServiceName || 'N/A'}
                                </Typography>
                            )}
                        </DetailTile>
                    </Grid>
                    <Grid item xs={6}>
                        <DetailTile
                            label="No. of Parcels"
                            sx={{
                                borderColor: isEditingShipment ? 'secondary.light' : 'divider',
                                backgroundColor: isEditingShipment ? 'rgba(220, 0, 78, 0.04)' : '#fcfdff',
                            }}
                        >
                            {isEditingShipment ? (
                                <>
                                    <TextField
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={numberOfParcels}
                                        onChange={(e) => setNumberOfParcels(e.target.value)}
                                        inputProps={{ min: 1, max: totalQty, step: 1 }}
                                    />
                                    {parcelCountError && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {parcelCountError}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {numberOfParcels || NumberOfParcels || 'N/A'}
                                </Typography>
                            )}
                        </DetailTile>
                    </Grid>
                    <Grid item xs={6}>
                        <DetailTile label="KornitX Status">
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                {checkingStatus ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <Chip
                                        label={kornitxOrderStatus}
                                        color={kornitxStatusColorMapping[kornitxOrderStatus] || 'default'}
                                        size="small"
                                        sx={{ fontWeight: 700 }}
                                    />
                                )}
                                <Button size="small" variant="outlined" onClick={fetchKornitxStatus}>
                                    <SyncIcon fontSize="small" />
                                </Button>
                                {kornitxOrderStatus === 'Not Found' && (
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                        Mintsoft dispatch still allowed
                                    </Typography>
                                )}
                            </Stack>
                        </DetailTile>
                    </Grid>
                    {hasGiftMessage && (
                        <Grid item xs={12}>
                            <DetailTile label="Gift Message">
                                <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', fontWeight: 600, color: 'text.primary' }}
                                >
                                    {he.decode(GiftMessages)}
                                </Typography>
                            </DetailTile>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default OrderDetails;
